/**
 * Admin Invitation Hooks
 * Query and manage pending user invitations
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { Invitation } from '@property-reports/api-contract';

const API_BASE = import.meta.env.VITE_API_URL || '';

async function fetchInvitations(tenantId?: string): Promise<Invitation[]> {
  const url = new URL(`${API_BASE}/api/admin/invitations`, window.location.origin);
  if (tenantId) {
    url.searchParams.set('tenantId', tenantId);
  }

  const response = await fetch(url.toString(), {
    credentials: 'include',
  });

  if (!response.ok) {
    throw new Error('Failed to fetch invitations');
  }

  return response.json();
}

async function resendInvitation(invitationId: string): Promise<{ success: boolean; message?: string }> {
  const response = await fetch(`${API_BASE}/api/admin/invitations/${invitationId}/resend`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({}),
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Failed to resend invitation');
  }

  return response.json();
}

async function cancelInvitation(invitationId: string): Promise<{ success: boolean; message?: string }> {
  const response = await fetch(`${API_BASE}/api/admin/invitations/${invitationId}`, {
    method: 'DELETE',
    credentials: 'include',
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.message || 'Failed to cancel invitation');
  }

  return response.json();
}

/**
 * Hook to fetch pending invitations
 */
export function useAdminInvitations(tenantId?: string) {
  return useQuery({
    queryKey: ['admin', 'invitations', tenantId],
    queryFn: () => fetchInvitations(tenantId),
  });
}

/**
 * Hook to resend an invitation
 */
export function useResendInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: resendInvitation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'invitations'] });
    },
  });
}

/**
 * Hook to cancel an invitation
 */
export function useCancelInvitation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: cancelInvitation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'invitations'] });
    },
  });
}
